//NPM
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { omit, omitBy, isNull, uniqWith } from "lodash";

// UTILITY
import errorHandler from "Utils/error-handler";
import { AssignDriverSchema } from "./assign-driver-validation";

// HELPERS
import { toaster } from "Helpers/toaster";
import { toNum } from "Helpers/get-to-num";

//STYLE
import "../parcel-tracking-modals.scss";
import { CalendarOutlined } from "@ant-design/icons";

// HOOKS
import useSession from "Hooks/session.hook";

//COMPONENTS
import { Col, Row } from "antd";
import { AlertBox, Button, SelectInput, TextInput } from "@life-line-lab/components-react";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";

interface AssignDriverModalProps {
  vehicleOptions: any;
  selectedRows?: any;
  info?: any;
  type: string;
  handleCancel: any;
  onSubmit: any;
}

const AssignDriverModal: React.FC<AssignDriverModalProps> = ({
  vehicleOptions = [],
  selectedRows,
  info,
  type,
  handleCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  //Note: 3RD party driver was catered in this modal, but it is not needed at the moment
  const initialInput = {
    companyType: "COMPANY",
    pickupIDs: selectedRows ? selectedRows : [],
    thirdPartyCompany: "",
    vehicleID: "",
    driverID: "",
    driverName: "",
  };
  const initialError = {};
  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [totalParcels, setTotalParcels] = useState<any>(0);

  // prevent customer duplicates by comparing customer and address id
  const allCustomers = info?.map((customer) => customer?.pickupTasks?.map((task) => task));
  const customers = uniqWith(
    allCustomers,
    (customerOne: any, customerTwo: any) =>
      customerOne?.merchantID == customerTwo?.merchantID &&
      customerOne?.locationID == customerTwo?.locationID,
  );

  useEffect(() => {
    let temp = 0;
    info.map((i) => {
      temp = temp + toNum(i.noOfParcels);
    });
    setTotalParcels(temp);
  }, [info]);

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({ ...error, [name]: null });

    let tempInput = { ...input, [name]: value };

    if (name === "vehicleID") {
      // autopopulate the driver id details
      const hasDriver = vehicleOptions.find((v) => v._id === value)?.driver;

      if (hasDriver) {
        tempInput = {
          ...tempInput,
          driverID: hasDriver?.profileID,
          driverName: hasDriver?.fullName,
        };
      }
    }

    setInput(tempInput);
  };

  const handleSubmit = () => {
    try {
      const valDiolkoInput = {
        ...input,
        assignedDriver: input.driverID,
      };
      const value = AssignDriverSchema.validateSync(omitBy(valDiolkoInput, isNull), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });

      const valThirdPartyInput = {
        pickupID: input.pickupIDs,
        assignedDriver: input.thirdPartyCompany,
      };

      input.companyType == "COMPANY" ? onSubmit(value) : onSubmit(valThirdPartyInput);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
    }
  };

  return (
    <div className="parcel-tracking-modal">
      <ModuleTitleBar
        titleText={
          type == "ASSIGN"
            ? t("title.assignDriver").toUpperCase()
            : t("title.reassignDriver").toUpperCase()
        }
      />
      <strong>{t("title.customerAndLocation").toUpperCase()}</strong>
      <div className="ptm-scrollable">
        <Row className="mt-10" gutter={24}>
          {customers[0]?.map((task) => {
            const street1 = task?.locationAddress?.street1 ?? task?.address?.street1;
            const street2 = task?.locationAddress?.street2 ?? task?.address?.street2;
            const postcode = task?.locationAddress?.postcode ?? task?.address?.postcode;
            const city = task?.locationAddress?.city ?? task?.address?.city;
            const state = task?.locationAddress?.state ?? task?.address?.state;
            const country = task?.locationAddress?.country ?? task?.address?.country;

            return (
              <Col span={24}>
                <div>
                  <div className="put-name">{task?.merchantName}</div>
                  <div className="put-address">
                    <div>
                      {street1 + ", "}
                      {street2 ? street2 + ", " : ""}
                    </div>
                    <div>
                      {postcode + ", "}
                      {city + ", "}
                    </div>
                    <div>
                      {state + ", "}
                      {country}
                    </div>
                  </div>
                </div>
                <AlertBox text={error.pickupIDs} />
              </Col>
            );
          })}
        </Row>
      </div>
      <Row>
        <strong>TOTAL PARCELS: &nbsp;</strong>
        {totalParcels}
      </Row>
      {/* <div className="mt-20">
        <SelectInput
          label={t("label.selectCompanyType")}
          options={[
            { label: 'DIOLKO', value: 'COMPANY' },
            { label: '3rd Party', value: 'THIRDPARTY' }
          ]}
          isRequired
          value={input.companyType}
          name="companyType"
          placeholder={t("txt.selectHere")}
          onChange={(value: string | string[]) => handleSelectChange(value, "companyType")}
        />
      </div> */}
      {
        input.companyType == "COMPANY" ? (
          <div>
            <div className="mt-20">
              <SelectInput
                showSearch
                filterOption={(inputValue, option) =>
                  option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                }
                label={t("label.selectVehicle")}
                options={vehicleOptions?.map((vehicle) => ({
                  label: vehicle.name,
                  value: vehicle._id,
                }))}
                isRequired
                value={input.vehicleID}
                name="vehicleType"
                placeholder={t("txt.selectHere")}
                onChange={(value: string | string[]) => handleSelectChange(value, "vehicleID")}
              />
              <AlertBox text={error.vehicleID} />
            </div>
            <div className="mt-10">
              <TextInput
                value={input.driverName}
                isReadonly={true}
                label={t("title.assignDriver")}
              />

              {/* <SelectInput
                isReadonly={true}
                label={t("title.assignDriver")}
                options={vehicleOptions?.map((vehicle) => ({
                  label: vehicle?.driver?.fullName,
                  value: vehicle._id,
                }))}
                value={input.driverID}
                name="driver"
                placeholder={t("txt.selectHere")}
                onChange={(value: string | string[]) => handleSelectChange(value, "driverID")}
              /> */}
            </div>
          </div>
        ) : (
          <></>
        )
        // <div>
        //   <div className="mt-10">
        //     <SelectInput
        //       label={t("label.selectVehicleType")}
        //       options={[
        //         { label: 'CAR', value: 'car' },
        //         { label: 'VAN', value: 'van' },
        //         { label: 'BIKE', value: 'bike' }
        //       ]}
        //       isRequired
        //       value={input.vehicleType}
        //       name="vehicleType"
        //       placeholder={t("txt.selectHere")}
        //       onChange={(value: string | string[]) => handleSelectChange(value, "vehicleType")}
        //     />
        //   </div>
        //   <div className="mt-10">
        //     <TextInput
        //       isRequired={true}
        //       label={t("label.thirdPartyName")}
        //       placeholder={t("txt.thirdPartyName")}
        //       value={input.thirdPartyCompany}
        //       name="thirdPartyCompany"
        //       onChange={handleFieldChange}
        //     />
        //   </div>
        // </div>
      }

      <div className="mt-30 right">
        <Button
          type={"primary"}
          label={t("action.confirm")}
          onClick={() => {
            handleSubmit();
          }}
        />
        <Button
          className="cancel-btn"
          type={"default"}
          label={t("action.cancel")}
          onClick={() => {
            handleCancel();
          }}
        />
      </div>
    </div>
  );
};

export default AssignDriverModal;
